import { BlueprintInstanceType } from '../shared.enums';
import { PanelItemGroup } from './panel-item-group.class';
import { Vertex3 } from '../../spatial-classes';

import { KitchenDesignBase, KitchenDesignOptions } from '../kitchen-design-base';
import { PanelItemGroupsDataIn, PanelItemGroupsDataOut } from './types';

export class PanelItemGroups extends KitchenDesignBase<PanelItemGroupsDataIn> {
	readonly children: Array<PanelItemGroup>;

	constructor (options: KitchenDesignOptions<PanelItemGroupsDataIn> = {}) {
		super(BlueprintInstanceType.PanelItemGroups, options);

		const { dataIn: data = null } = options;
		this.children = [];

		if (data && data.children && data.children.length > 0) {
			data.children.forEach((n) => this.children.push(new PanelItemGroup({ dataIn: n, parent: this })));
		}

		this.setInitialized();
	}

	/**
	 * max distanceAbove countertop for all panel item groups
	 * @returns number
	 */
	public getMaxDistanceAboveCountertop (): number {
		return this.children.reduce((acc, itemGroup) => Math.max(acc, itemGroup.getMaxDistanceAboveCountertop()), 0);
	}

	/**
	 * locates the panel item group whos center is closest to, but less than the provided center
	 * @returns PanelItemGroup or null when an edge is reaches
	 */
	public findPreviousByCenter (center: Vertex3, withCountertopItems = false): PanelItemGroup {
		const lessThan = this.children
			.filter((pi) => pi.center.x < center.x && (!withCountertopItems || pi.items.filterItems(withCountertopItems).length > 0))
			.sort((a, b) => a.center.x - b.center.x);
		// console.log('findPreviousByCenter', this.map((i) => i.items), lessThan.map((i) => i.items));
		return lessThan.length === 0 ? null : lessThan[lessThan.length - 1] as PanelItemGroup;
	}

	/**
	 * locates the panel item group whos center is closest to, but greater than the provided center
	 * @returns PanelItemGroup or null when an edge is reached
	 */
	public findNextByCenter (center: Vertex3, withCountertopItems = false): PanelItemGroup {
		const greaterThan = this.children
			.filter((pi) => pi.center.x > center.x && (!withCountertopItems || pi.items.filterItems(withCountertopItems).length > 0))
			.sort((a, b) => a.center.x - b.center.x);
		// console.log('findNextByCenter', this.map((i) => i.items), greaterThan.map((i) => i.items));
		return greaterThan.length === 0 ? null : greaterThan[0] as PanelItemGroup;
	}

	/**
	 * returns an instance of this sorted by center location
	 * @returns PanelItemGrops
	 */
	public sortByCenter (): Array<PanelItemGroup> { return this.children.sort((a, b) => a.center.x - b.center.x) as Array<PanelItemGroup>; }

	/**
	 * Creates and adds a new panelItemGroup
	 *
	 * @returns Instance of PanelItemGroup
	 */
	// @Publish('panel_item_group_created')
	public new (): PanelItemGroup {
		const panelItemGroup = new PanelItemGroup({
			parent: this
		});
		this.children.push(panelItemGroup);

		return panelItemGroup;
	}

	/**
	 * Purges any panelItemGroup that doesn't have any panelItems associated with it
	 * @returns number of panelItemGroups deleted
	 */
	public purgeEmpty (): number {
		const emptyItems = this.children.filter((f) => f.items.children.length === 0);
		// console.log('panelItemGroups:purgeEmpty', emptyItems);
		emptyItems.forEach((i) => {
			this.children.splice(this.children.indexOf(i), 1);
		});
		return emptyItems.length;
	}

	/**
	 * Serialize the panel item groups to data output format
	 * @returns PanelItemGroupsDataOut
	 */
	public serialize (): PanelItemGroupsDataOut {
		return {
			children: this.children.map((group) => group.serialize())
		};
	}
}
